import { z } from "zod";
import { ServiceError, ServiceErrorCode } from "./serviceError";
import { validationError } from "./validationError";

const serviceValidationFailureBody = z.object({
  validationErrors: z.array(validationError).min(1),
});

/**
 * Parse request args with Zod. Throws ServiceError MALFORMED_INPUT on failure.
 */
export function parseServiceInput<T>(schema: z.ZodType<T>, args: unknown): T {
  const input = schema.safeParse(args);
  if (!input.success) {
    throw new ServiceError("Input is malformed", {
      code: ServiceErrorCode.MALFORMED_INPUT,
      statusCode: 400,
      details: { issues: input.error.issues },
    });
  }
  return input.data;
}

/**
 * Read a failed HTTP response and throw a typed ServiceError.
 * Prefer VALIDATION_ERROR when the body includes domain validationErrors.
 */
export async function throwFromFailedResponse(
  response: Response,
  caller?: string,
): Promise<never> {
  const raw = await response.text();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = undefined;
  }

  const validationBody = serviceValidationFailureBody.safeParse(parsed);
  if (validationBody.success) {
    throw new ServiceError("Validation failed", {
      code: ServiceErrorCode.VALIDATION_ERROR,
      statusCode: response.status,
      details: {
        validationErrors: validationBody.data.validationErrors,
      },
    });
  }

  throw new ServiceError("An error occurred", {
    code: ServiceErrorCode.SERVICE_ERROR,
    statusCode: response.status,
    details: { error: raw },
  });
}

/**
 * Ensure response is OK, then parse JSON with Zod.
 * Throws VALIDATION_ERROR / SERVICE_ERROR on HTTP failure,
 * or the provided malformed code when the body shape is unexpected.
 */
export async function parseServiceOutput<T>(
  response: Response,
  schema: z.ZodType<T>,
  malformed: { code: string; message: string; caller?: string },
): Promise<T> {
  if (!response.ok) {
    await throwFromFailedResponse(response, malformed.caller);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ServiceError(malformed.message, {
      code: malformed.code,
      statusCode: response.status,
      details: { error: "Invalid JSON response" },
    });
  }

  const output = schema.safeParse(payload);
  if (!output.success) {
    throw new ServiceError(malformed.message, {
      code: malformed.code,
      statusCode: response.status,
      details: { issues: output.error.issues },
    });
  }

  return output.data;
}
